import { Link } from 'gatsby';
import type { FC, PropsWithChildren } from 'react';
import { use } from 'react';

import { getClubInformation } from 'src/content/club-look-ups-by-id';
import { GlobalCmsContent } from 'src/content/global-content';
import { SiteContext } from 'src/context/site-context';
import type { Styling } from 'src/context/styling';
import { useClientSnapshot } from 'src/custom-hooks/use-client-snapshot';
import { useCrossClubSharedSummary } from 'src/custom-hooks/use-cross-club-shared-summary';
import type { ClubInformation } from 'src/enums/club-information';
import { clubNameDependingOnBuildTypeAndLanguage } from 'src/helpers/club-helper';
import { getImageForClubFinder } from 'src/helpers/hosted-asset-helpers';
import { getLocalStorageItem } from 'src/helpers/local-storage-helper';
import {
  getAbsoluteClubPagePathFromClubInformation,
  getClubPagePathFromClubInformation,
} from 'src/helpers/navigation-helper';
import { RecentlyViewedClubStorageKey } from 'src/helpers/recently-viewed-club-storage-keys';
import { trimLocationString } from 'src/helpers/string-helper';
import { flex } from 'src/styling/constants';
import mq from 'src/styling/media-queries';
import { TranslatedStaticText } from 'src/translators/static-text-translator';

const createStyling = (styling: Styling) => ({
  mainTitleStyling: mq([
    styling.fonts.regular,
    {
      fontSize: '1rem',
      lineHeight: '145%',
      color: styling.colors.capeCod,
      padding: '0.75rem 0',
      margin: ['2.5rem 0 1.5rem', '2rem 0 0.75rem'],
      borderBottom: `1px solid ${styling.colors.grayOlive}`,
    },
  ]),
  containerStyling: mq({
    padding: 0,
  }),
  wrapperStyling: mq([
    flex.row,
    {
      'padding': '0.75rem 0',
      '&:hover': {
        '.club-name': {
          color: styling.colors.camelot,
          textDecoration: 'underline',
        },
      },
    },
  ]),
  imageStyling: mq({
    minWidth: '5rem',
    width: '5rem',
    height: '5rem',
    objectFit: 'cover',
    borderRadius: '0.29088rem',
  }),
  titleStyling: mq([
    styling.fonts.regular,
    {
      fontSize: '1.5rem',
      textTransform: 'capitalize',
      lineHeight: '145%',
      color: styling.colors.capeCod,
    },
  ]),
  comingSoonStyling: mq([
    styling.fonts.bold,
    {
      color: styling.colors.camelot,
      marginLeft: '0.75rem',
      fontSize: '1.1875rem',
    },
  ]),
  addressStyling: mq([
    styling.fonts.regular,
    {
      fontSize: '1rem',
      lineHeight: '145%',
      color: styling.colors.capeCod,
      marginBottom: '0.15rem',
    },
  ]),
  linkStyle: mq([
    styling.common.link,
    {
      fontSize: '1rem',
      lineHeight: '145%',
      textDecorationLine: 'underline',
      textDecorationStyle: 'solid',
      textDecorationSkipLnk: 'none',
      textDecorationThickness: 'auto',
      textUnderlineOffset: '35%',
      textUnderlinePosition: 'from-font',
    },
  ]),
});

export const RecentlyVisitedClubInfo: FC = () => {
  const clubId = useClientSnapshot(
    () => getLocalStorageItem(RecentlyViewedClubStorageKey.searchClubId) ?? null,
    null
  );

  return !!clubId && <RecentlyVisitedClubInfoContent clubId={clubId} />;
};

const RecentlyVisitedClubInfoContent: FC<{ clubId: string }> = ({ clubId }) => {
  const { styling, buildType, language } = use(SiteContext);
  const { headerContent } = use(GlobalCmsContent);
  const componentStyle = createStyling(styling);
  const clubInformation = getClubInformation(clubId);
  const clubName = clubInformation.clubName;

  const shared = useCrossClubSharedSummary(clubId);
  const phone = shared.phoneNumbers.nonMembersPhoneNumber;

  if (!headerContent?.searchSettings?.recentlyViewedClubCaption) {
    return null;
  }

  return (
    <div css={componentStyle.containerStyling}>
      <div css={componentStyle.mainTitleStyling}>
        {headerContent.searchSettings.recentlyViewedClubCaption}
      </div>
      <div css={componentStyle.wrapperStyling}>
        <LinkComponent clubInformation={clubInformation}>
          <img
            src={getImageForClubFinder(shared.clubThumbnailImage)}
            alt={clubName}
            css={componentStyle.imageStyling}
          />
        </LinkComponent>
        <div css={{ marginLeft: '0.75rem', marginTop: '-0.4rem' }}>
          <LinkComponent clubInformation={clubInformation}>
            <span className="club-name" css={componentStyle.titleStyling}>
              {clubNameDependingOnBuildTypeAndLanguage(clubName, buildType, language)}
              {shared.comingSoonClubFinder && (
                <span css={componentStyle.comingSoonStyling}>
                  <TranslatedStaticText lookupKey={'comingSoon'} />
                </span>
              )}
            </span>
            <div css={componentStyle.addressStyling}>
              {trimLocationString(shared.clubLocation.text1!, shared.clubLocation.text2!) ?? ''}
            </div>
          </LinkComponent>
          <a
            css={componentStyle.linkStyle}
            href={`tel:${phone}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {phone}
          </a>
        </div>
      </div>
    </div>
  );
};

const LinkComponent: FC<PropsWithChildren<{ clubInformation: ClubInformation }>> = ({
  clubInformation,
  children,
}) => {
  const { buildType, language } = use(SiteContext);
  return !!clubInformation && clubInformation.buildType === buildType ? (
    <Link to={getClubPagePathFromClubInformation('', clubInformation, language)}>{children}</Link>
  ) : (
    <a href={getAbsoluteClubPagePathFromClubInformation('', clubInformation, language)}>
      {children}
    </a>
  );
};
